import * as React from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit2,
  Network,
  Plus,
  Search,
  Trash2,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  DataCard,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardList,
  DataCardTitle,
} from "@/components/ui/data-card"
import type { PageProps } from "@/types/inertia"

type OrgEmployee = {
  id: number
  name: string
  job_title: string
  department: string
  email: string
  work_location: string | null
  company: string | null
  work_hours: string | null
  manager_id: number | null
  manager_name: string | null
}

type Pagination = {
  total_count: number
  page: number
  per_page: number
}

type Filters = {
  search: string | null
  department: string | null
  sort_by: string | null
  sort_order: string | null
}

type Props = {
  employees: OrgEmployee[]
  departments: string[]
  managers: { id: number; name: string }[]
  pagination: Pagination
  filters: Filters
}

export default function AdminOrgChartIndex() {
  const { props } = usePage<PageProps<Props>>()
  const { employees, departments, managers, pagination, filters } = props

  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [searchValue, setSearchValue] = React.useState(filters.search ?? "")
  const [addOpen, setAddOpen] = React.useState(false)
  const [editEmployee, setEditEmployee] = React.useState<OrgEmployee | null>(null)
  const [deleteEmployee, setDeleteEmployee] = React.useState<OrgEmployee | null>(null)

  // Sync search input with filter prop changes
  React.useEffect(() => {
    setSearchValue(filters.search ?? "")
  }, [filters.search])

  const totalPages = Math.ceil(pagination.total_count / pagination.per_page)

  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    if (filters.search) params.search = filters.search
    if (filters.department) params.department = filters.department
    if (filters.sort_by) params.sort_by = filters.sort_by
    if (filters.sort_order) params.sort_order = filters.sort_order
    if (pagination.page > 1) params.page = pagination.page

    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") {
        params[k] = v
      } else {
        delete params[k]
      }
    })

    setSelectedIds(new Set())
    router.get("/admin/org-chart", params as Record<string, string>, {
      preserveScroll: true,
    })
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    navigate({ search: searchValue || null, page: null })
  }

  function handleDepartmentChange(e: React.ChangeEvent<HTMLSelectElement>) {
    navigate({ department: e.target.value || null, page: null })
  }

  function handleSortColumn(col: string) {
    if (filters.sort_by === col) {
      navigate({
        sort_order: filters.sort_order === "asc" ? "desc" : "asc",
        page: null,
      })
    } else {
      navigate({ sort_by: col, sort_order: "asc", page: null })
    }
  }

  // Checkbox functions
  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === employees.length && employees.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(employees.map((emp) => emp.id)))
    }
  }

  function handleBulkDelete() {
    if (selectedIds.size === 0) return
    if (!window.confirm(`Apakah Anda yakin ingin menghapus ${selectedIds.size} pegawai terpilih?`)) {
      return
    }

    router.delete("/admin/org-chart/bulk_destroy", {
      data: { ids: Array.from(selectedIds) },
      onFinish: () => setSelectedIds(new Set()),
    })
  }

  // useForm Hooks for CRUD
  const addForm = useForm({
    name: "",
    job_title: "",
    department: "",
    email: "",
    work_location: "",
    company: "",
    work_hours: "",
    manager_id: "",
  })

  const editForm = useForm({
    name: "",
    job_title: "",
    department: "",
    email: "",
    work_location: "",
    company: "",
    work_hours: "",
    manager_id: "",
  })

  // Sync Edit form when edit employee changes
  React.useEffect(() => {
    if (editEmployee) {
      editForm.setData({
        name: editEmployee.name ?? "",
        job_title: editEmployee.job_title ?? "",
        department: editEmployee.department ?? "",
        email: editEmployee.email ?? "",
        work_location: editEmployee.work_location ?? "",
        company: editEmployee.company ?? "",
        work_hours: editEmployee.work_hours ?? "",
        manager_id: editEmployee.manager_id ? String(editEmployee.manager_id) : "",
      })
    }
  }, [editEmployee])

  function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault()
    addForm.post("/admin/org-chart", {
      onSuccess: () => {
        addForm.reset()
        setAddOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editEmployee) return
    editForm.patch(`/admin/org-chart/${editEmployee.id}`, {
      onSuccess: () => {
        editForm.reset()
        setEditEmployee(null)
      },
    })
  }

  function handleDeleteConfirm() {
    if (!deleteEmployee) return
    router.delete(`/admin/org-chart/${deleteEmployee.id}`, {
      onFinish: () => setDeleteEmployee(null),
    })
  }

  const notice = props.flash?.notice
  const alert = props.flash?.alert

  return (
    <>
      <Head title="Org Chart">
        <meta
          name="description"
          content="Kelola struktur organisasi dan data pegawai perusahaan."
        />
        <meta property="og:title" content="Org Chart" />
        <meta
          property="og:description"
          content="Kelola struktur organisasi dan data pegawai perusahaan."
        />
      </Head>

      <AppShell>
        <PageHeader
          title="Org Chart"
          description="Kelola struktur organisasi dan detail kontak pegawai."
          actions={
            <div className="flex items-center gap-2">
              <Button asChild variant="secondary" size="sm">
                <Link href="/admin/org-chart/tree" className="no-underline">
                  <Network className="mr-1.5 h-4 w-4" />
                  Lihat Bagan Tree
                </Link>
              </Button>
              <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                Tambah Pegawai
              </Button>
            </div>
          }
        />

        {/* Notices */}
        {notice && <p className="mt-4 text-sm text-accent">{notice}</p>}
        {alert && <p className="mt-4 text-sm text-danger-display">{alert}</p>}

        {/* Filters and Search Bar */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={filters.department ?? ""}
              onChange={handleDepartmentChange}
              className="w-48"
            >
              <option value="">Semua Departemen</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </Select>

            {(filters.search || filters.department || filters.sort_by) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({ search: null, department: null, sort_by: null, sort_order: null })
                }
              >
                Reset filter
              </Button>
            )}
          </div>

          <form onSubmit={handleSearchSubmit} className="relative w-full max-w-xs sm:w-auto">
            <Input
              type="search"
              placeholder="Cari nama atau email..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={() => navigate({ search: searchValue || null, page: null })}
              className="pl-9 pr-4"
            />
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted pointer-events-none" />
          </form>
        </div>

        {/* Bulk Action Toolbar */}
        {selectedIds.size > 0 && (
          <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-hairline bg-surface px-4 py-2">
            <span className="text-sm font-medium text-ink-display">
              {selectedIds.size} pegawai terpilih
            </span>
            <div className="flex items-center gap-2">
              <Button variant="danger" size="sm" onClick={handleBulkDelete}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Hapus Terpilih
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Batal
              </Button>
            </div>
          </div>
        )}

        {/* Employees Grid/Table */}
        {employees.length === 0 ? (
          <div className="mt-6 rounded-md border border-hairline bg-page p-8 text-center text-ink-muted">
            Tidak ada pegawai yang ditemukan.
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden mt-6 overflow-hidden rounded-md border border-hairline md:block bg-page">
              <table className="w-full text-sm">
                <thead className="bg-surface">
                  <tr>
                    <th className="w-10 px-3 py-2.5">
                      <Checkbox
                        checked={employees.length > 0 && selectedIds.size === employees.length}
                        onChange={toggleSelectAll}
                        aria-label="Pilih semua"
                      />
                    </th>
                    <SortableHeader
                      col="name"
                      label="Nama"
                      currentSort={filters.sort_by}
                      currentOrder={filters.sort_order}
                      onSort={handleSortColumn}
                    />
                    <SortableHeader
                      col="job_title"
                      label="Jabatan"
                      currentSort={filters.sort_by}
                      currentOrder={filters.sort_order}
                      onSort={handleSortColumn}
                    />
                    <SortableHeader
                      col="department"
                      label="Departemen"
                      currentSort={filters.sort_by}
                      currentOrder={filters.sort_order}
                      onSort={handleSortColumn}
                    />
                    <SortableHeader
                      col="email"
                      label="Email"
                      currentSort={filters.sort_by}
                      currentOrder={filters.sort_order}
                      onSort={handleSortColumn}
                    />
                    <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Manager</th>
                    <th className="w-20 px-3 py-2.5 text-right font-medium text-ink-muted">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {employees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-surface/20">
                      <td className="px-3 py-2.5">
                        <Checkbox
                          checked={selectedIds.has(emp.id)}
                          onChange={() => toggleSelect(emp.id)}
                          aria-label={`Pilih ${emp.name}`}
                        />
                      </td>
                      <td className="px-4 py-2.5 font-medium text-ink-display">
                        <div className="flex items-center gap-2.5">
                          <InitialsBadge name={emp.name} />
                          <div>{emp.name}</div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-ink-body">{emp.job_title}</td>
                      <td className="px-4 py-2.5 text-ink-body">{emp.department}</td>
                      <td className="px-4 py-2.5 text-ink-muted">{emp.email}</td>
                      <td className="px-4 py-2.5 text-ink-body">
                        {emp.manager_name ?? <span className="text-ink-muted">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditEmployee(emp)}
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4 text-ink-muted hover:text-ink-body" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteEmployee(emp)}
                            title="Hapus"
                          >
                            <Trash2 className="h-4 w-4 text-ink-muted hover:text-danger-display" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards List View */}
            <div className="mt-6 md:hidden">
              <DataCardList>
                {employees.map((emp) => (
                  <DataCard key={emp.id}>
                    <DataCardHeader>
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedIds.has(emp.id)}
                          onChange={() => toggleSelect(emp.id)}
                          aria-label={`Pilih ${emp.name}`}
                        />
                        <InitialsBadge name={emp.name} />
                        <DataCardTitle className="font-semibold">{emp.name}</DataCardTitle>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditEmployee(emp)}
                        >
                          <Edit2 className="h-4 w-4 text-ink-muted" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteEmployee(emp)}
                        >
                          <Trash2 className="h-4 w-4 text-ink-muted" />
                        </Button>
                      </div>
                    </DataCardHeader>
                    <DataCardGrid>
                      <DataCardField label="Jabatan" value={emp.job_title} />
                      <DataCardField label="Departemen" value={emp.department} />
                      <DataCardField label="Email" value={emp.email} wide />
                      <DataCardField
                        label="Manager"
                        value={emp.manager_name ?? "—"}
                      />
                      <DataCardField
                        label="Lokasi"
                        value={emp.work_location ?? "—"}
                      />
                    </DataCardGrid>
                  </DataCard>
                ))}
              </DataCardList>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => navigate({ page: pagination.page - 1 })}
                >
                  Sebelumnya
                </Button>
                <span className="text-sm text-ink-muted">
                  Halaman {pagination.page} dari {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page >= totalPages}
                  onClick={() => navigate({ page: pagination.page + 1 })}
                >
                  Selanjutnya
                </Button>
              </div>
            )}
          </>
        )}

        {/* Dialog Add Employee */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>Tambah Pegawai Baru</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSubmit} className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label htmlFor="add_name">Nama Lengkap</label>
                  <Input
                    id="add_name"
                    required
                    value={addForm.data.name}
                    onChange={(e) => addForm.setData("name", e.target.value)}
                  />
                  {addForm.errors.name && (
                    <p className="text-xs text-danger-display">{addForm.errors.name}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="add_email">Email</label>
                  <Input
                    id="add_email"
                    type="email"
                    required
                    value={addForm.data.email}
                    onChange={(e) => addForm.setData("email", e.target.value)}
                  />
                  {addForm.errors.email && (
                    <p className="text-xs text-danger-display">{addForm.errors.email}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="add_job_title">Jabatan</label>
                  <Input
                    id="add_job_title"
                    required
                    value={addForm.data.job_title}
                    onChange={(e) => addForm.setData("job_title", e.target.value)}
                  />
                  {addForm.errors.job_title && (
                    <p className="text-xs text-danger-display">{addForm.errors.job_title}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="add_department">Departemen</label>
                  <Input
                    id="add_department"
                    required
                    value={addForm.data.department}
                    onChange={(e) => addForm.setData("department", e.target.value)}
                  />
                  {addForm.errors.department && (
                    <p className="text-xs text-danger-display">{addForm.errors.department}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="add_work_location">Lokasi Kerja</label>
                  <Input
                    id="add_work_location"
                    value={addForm.data.work_location}
                    onChange={(e) => addForm.setData("work_location", e.target.value)}
                  />
                  {addForm.errors.work_location && (
                    <p className="text-xs text-danger-display">{addForm.errors.work_location}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="add_company">Perusahaan</label>
                  <Input
                    id="add_company"
                    value={addForm.data.company}
                    onChange={(e) => addForm.setData("company", e.target.value)}
                  />
                  {addForm.errors.company && (
                    <p className="text-xs text-danger-display">{addForm.errors.company}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="add_work_hours">Jam Kerja</label>
                  <Input
                    id="add_work_hours"
                    placeholder="Contoh: 08:00 - 17:00"
                    value={addForm.data.work_hours}
                    onChange={(e) => addForm.setData("work_hours", e.target.value)}
                  />
                  {addForm.errors.work_hours && (
                    <p className="text-xs text-danger-display">{addForm.errors.work_hours}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="add_manager_id">Manager / Atasan</label>
                  <Select
                    id="add_manager_id"
                    value={addForm.data.manager_id}
                    onChange={(e) => addForm.setData("manager_id", e.target.value)}
                  >
                    <option value="">Tidak ada (Root / Paling Atas)</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </Select>
                  {addForm.errors.manager_id && (
                    <p className="text-xs text-danger-display">{addForm.errors.manager_id}</p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Batal</Button>
                </DialogClose>
                <Button variant="primary" type="submit" disabled={addForm.processing}>
                  {addForm.processing ? "Menyimpan..." : "Tambah Pegawai"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Edit Employee */}
        <Dialog open={editEmployee !== null} onOpenChange={(open) => !open && setEditEmployee(null)}>
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>Edit Pegawai</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label htmlFor="edit_name">Nama Lengkap</label>
                  <Input
                    id="edit_name"
                    required
                    value={editForm.data.name}
                    onChange={(e) => editForm.setData("name", e.target.value)}
                  />
                  {editForm.errors.name && (
                    <p className="text-xs text-danger-display">{editForm.errors.name}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="edit_email">Email</label>
                  <Input
                    id="edit_email"
                    type="email"
                    required
                    value={editForm.data.email}
                    onChange={(e) => editForm.setData("email", e.target.value)}
                  />
                  {editForm.errors.email && (
                    <p className="text-xs text-danger-display">{editForm.errors.email}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="edit_job_title">Jabatan</label>
                  <Input
                    id="edit_job_title"
                    required
                    value={editForm.data.job_title}
                    onChange={(e) => editForm.setData("job_title", e.target.value)}
                  />
                  {editForm.errors.job_title && (
                    <p className="text-xs text-danger-display">{editForm.errors.job_title}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="edit_department">Departemen</label>
                  <Input
                    id="edit_department"
                    required
                    value={editForm.data.department}
                    onChange={(e) => editForm.setData("department", e.target.value)}
                  />
                  {editForm.errors.department && (
                    <p className="text-xs text-danger-display">{editForm.errors.department}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="edit_work_location">Lokasi Kerja</label>
                  <Input
                    id="edit_work_location"
                    value={editForm.data.work_location}
                    onChange={(e) => editForm.setData("work_location", e.target.value)}
                  />
                  {editForm.errors.work_location && (
                    <p className="text-xs text-danger-display">{editForm.errors.work_location}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="edit_company">Perusahaan</label>
                  <Input
                    id="edit_company"
                    value={editForm.data.company}
                    onChange={(e) => editForm.setData("company", e.target.value)}
                  />
                  {editForm.errors.company && (
                    <p className="text-xs text-danger-display">{editForm.errors.company}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="edit_work_hours">Jam Kerja</label>
                  <Input
                    id="edit_work_hours"
                    placeholder="Contoh: 08:00 - 17:00"
                    value={editForm.data.work_hours}
                    onChange={(e) => editForm.setData("work_hours", e.target.value)}
                  />
                  {editForm.errors.work_hours && (
                    <p className="text-xs text-danger-display">{editForm.errors.work_hours}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="edit_manager_id">Manager / Atasan</label>
                  <Select
                    id="edit_manager_id"
                    value={editForm.data.manager_id}
                    onChange={(e) => editForm.setData("manager_id", e.target.value)}
                  >
                    <option value="">Tidak ada (Root / Paling Atas)</option>
                    {managers
                      .filter((m) => !editEmployee || m.id !== editEmployee.id)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                  </Select>
                  {editForm.errors.manager_id && (
                    <p className="text-xs text-danger-display">{editForm.errors.manager_id}</p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Batal</Button>
                </DialogClose>
                <Button variant="primary" type="submit" disabled={editForm.processing}>
                  {editForm.processing ? "Menyimpan..." : "Simpan Perubahan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Delete Single Confirmation */}
        <Dialog open={deleteEmployee !== null} onOpenChange={(open) => !open && setDeleteEmployee(null)}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Hapus Pegawai?</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-ink-body">
                Apakah Anda yakin ingin menghapus pegawai <strong>{deleteEmployee?.name}</strong>? Tindakan ini tidak dapat dibatalkan.
              </p>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Batal</Button>
                </DialogClose>
                <Button variant="danger" type="button" onClick={handleDeleteConfirm}>
                  Hapus
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </AppShell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------

function InitialsBadge({ name }: { name: string }) {
  const initials = React.useMemo(() => {
    if (!name) return ""
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }, [name])

  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground select-none"
      aria-hidden="true"
    >
      {initials}
    </div>
  )
}

function SortableHeader({
  col,
  label,
  currentSort,
  currentOrder,
  onSort,
}: {
  col: string
  label: string
  currentSort: string | null
  currentOrder: string | null
  onSort: (col: string) => void
}) {
  const isSorted = currentSort === col
  const Icon = isSorted ? (currentOrder === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown

  return (
    <th className="px-4 py-2.5 text-left font-medium text-ink-muted">
      <button
        type="button"
        onClick={() => onSort(col)}
        className={[
          "inline-flex items-center gap-1 hover:text-ink-body font-medium transition-colors",
          isSorted ? "text-ink-body" : "",
        ].join(" ")}
      >
        {label}
        <Icon className="h-3 w-3" />
      </button>
    </th>
  )
}
